// src/components/BannerDisplay.tsx
import { useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { ExternalLink } from 'lucide-react';
import { Link } from './Router';
import React from 'react';

// ─── Types ───────────────────────────────────────────────
interface BannerDisplayProps {
  pageLocation: string;
  positionKey: string;
  className?: string;
  fullBleed?: boolean;
  wrapperBgClass?: string;
}

interface Banner {
  id: string;
  title?: string;
  image_url?: string;
  video_url?: string;
  link_url?: string;
  link_type: 'internal' | 'external' | 'none';
  banner_type: string;
  dimensions: { width: string; height: string };
  alignment: 'left' | 'center' | 'right';
  margin_top: string;
  margin_bottom: string;
  margin_left: string;
  margin_right: string;
  content_type: 'image' | 'video';
  display_order: number;
  click_count?: number;
  view_count?: number;
}

function dailySeed(pageLocation: string, positionKey: string): number {
  const seed = `${new Date().toDateString()}-${pageLocation}-${positionKey}`;
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash) + seed.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

export const BannerDisplay = React.memo(function BannerDisplay({
  pageLocation,
  positionKey,
  className = '',
  fullBleed = false,
  wrapperBgClass = '',
}: BannerDisplayProps) {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [imageFailed, setImageFailed] = useState(false); // NEW: Tracks if the image breaks
  const rotationRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadBanners = useCallback(async () => {
    try {
      setLoading(true);
      setImageFailed(false); // Reset on load
      const { data, error } = await supabase
        .from('banners')
        .select('*')
        .eq('page_location', pageLocation)
        .eq('position_key', positionKey)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) { console.error('Banner load error:', error); setBanners([]); return; }
      if (!data || data.length === 0) { setBanners([]); return; }

      setBanners(data);
      const startIdx = dailySeed(pageLocation, positionKey) % data.length;
      setActiveIndex(startIdx);
      incrementView(data[startIdx].id);
    } catch (err) {
      console.error('Banner load error:', err);
      setBanners([]);
    } finally {
      setLoading(false);
    }
  }, [pageLocation, positionKey]);

  useEffect(() => { loadBanners(); }, [loadBanners]);

  useEffect(() => {
    return () => { if (rotationRef.current) clearInterval(rotationRef.current); };
  }, []);

  const incrementView = async (id: string) => {
    try {
      const { data } = await supabase.from('banners').select('view_count').eq('id', id).single();
      if (data) {
        await supabase.from('banners').update({
          view_count: (data.view_count || 0) + 1,
          updated_at: new Date().toISOString(),
        }).eq('id', id);
      }
    } catch (_) { /* silent */ }
  };

  const handleClick = async () => {
    if (!banners[activeIndex]) return;
    try {
      await supabase.from('banners').update({
        click_count: (banners[activeIndex].click_count || 0) + 1,
        updated_at: new Date().toISOString(),
      }).eq('id', banners[activeIndex].id);
    } catch (_) { /* silent */ }
  };

  const banner = banners[activeIndex];

  // ── Smart Dimension Parsing ──────────────────────────────────
  let containerStyle: React.CSSProperties = {
    marginTop: banner?.margin_top || '0px',
    marginBottom: banner?.margin_bottom || '0px',
    marginLeft: 'auto',
    marginRight: 'auto',
    overflow: 'hidden',
  };

  let imageClass = 'w-full block h-auto';

  if (banner?.dimensions) {
    const dims = typeof banner.dimensions === 'string' 
      ? JSON.parse(banner.dimensions) 
      : banner.dimensions;
    
    const wStr = String(dims.width || 'auto').trim();
    const hStr = String(dims.height || 'auto').trim();

    const wMatch = wStr.match(/^(\d+)(px)?$/);
    const hMatch = hStr.match(/^(\d+)(px)?$/);

    if (wMatch && hMatch) {
      const w = Number(wMatch[1]);
      const h = Number(hMatch[1]);
      containerStyle.width = '100%';
      containerStyle.maxWidth = `${w}px`;
      containerStyle.aspectRatio = `${w} / ${h}`;
      containerStyle.height = 'auto'; 
      imageClass = 'w-full h-full object-contain block';
    } else {
      containerStyle.width = wStr === 'auto' ? '100%' : wStr;
      containerStyle.height = hStr;
      if (wMatch) containerStyle.maxWidth = `${wMatch[1]}px`;
      
      if (hStr !== 'auto' && hStr !== '100%') {
        imageClass = 'w-full h-full object-contain block';
      }
    }
  }

  // ── Loading state ──────────────────────────────────────
  if (loading) {
    const skeleton = (
      <div className={`w-full animate-pulse ${className}`}>
        <div className="w-full bg-gray-100 rounded-xl" style={{ aspectRatio: containerStyle.aspectRatio || '16/4', minHeight: '80px' }} />
      </div>
    );
    if (wrapperBgClass) {
      return (
        <div className={`w-full ${wrapperBgClass}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">{skeleton}</div>
        </div>
      );
    }
    return skeleton;
  }

  // NEW: If there's no banner, OR if the image triggered an error, return absolutely nothing (no padding, no wrappers)
  if (banners.length === 0 || !banner || imageFailed) return null;

  // ── Media element ──────────────────────────────────────
  const MediaContent = () => {
    if (banner.content_type === 'video' && banner.video_url) {
      return (
        <video
          src={banner.video_url}
          autoPlay
          muted
          loop
          playsInline
          className={imageClass}
        />
      );
    }

    if (banner.image_url) {
      return (
        <img
          src={banner.image_url}
          alt={banner.title || 'Banner'}
          className={imageClass}
          loading="lazy"
          onError={() => {
            // NEW: If the image is broken, immediately trip the state to collapse the entire component
            setImageFailed(true);
          }}
        />
      );
    }

    return null;
  };

  // ── Wrapper with link handling ─────────────────────────
  const BannerMedia = () => {
    const media = (
      <div className="relative w-full h-full overflow-hidden group flex items-center justify-center">
        <MediaContent />
        {banner.link_url && banner.link_type !== 'none' && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/[0.03] transition-all duration-300 pointer-events-none" />
        )}
        {banner.link_type === 'external' && (
          <div className="absolute top-3 right-3 bg-white/80 backdrop-blur-sm rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-sm">
            <ExternalLink className="h-3 w-3 text-gray-500" />
          </div>
        )}
      </div>
    );

    if (banner.link_type === 'none' || !banner.link_url) {
      return media;
    }

    if (banner.link_type === 'external') {
      return (
        <a href={banner.link_url} target="_blank" rel="noopener noreferrer" className="block w-full h-full" onClick={handleClick}>
          {media}
        </a>
      );
    }

    return (
      <Link to={banner.link_url} className="block w-full h-full" onClick={handleClick}>
        {media}
      </Link>
    );
  };

  // ── Render ─────────────────────────────────────────────
  const content = (
    <div className={`w-full mx-auto ${className}`} style={{ maxWidth: containerStyle.maxWidth || '100%' }}>
      <div style={containerStyle}>
        <BannerMedia />
      </div>
    </div>
  );

  if (wrapperBgClass) {
    return (
      <div className={`w-full ${wrapperBgClass}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {content}
        </div>
      </div>
    );
  }

  return content;
});
